import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Box, Button } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetAudExchangeRates } from '~/hooks/apis/fx/useGetConvertedCostPricesInAud';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { useEgsDraftsContext, usePackageOptionsContext } from '../../PricingToolSessionContext';
import { deriveOpportunityCurrency } from '../inclusions/inclusionsModel';

import EgsTable from './EgsTable';
import EgsWizardModal from './EgsWizardModal';
import type { EgsDraft, EgsDraftInput } from './types';

interface Props {
  offerId?: string;
}

export default function EgsTab({ offerId }: Props) {
  const { data, error: relatedError, isLoading } = useGetOpportunityRelated(offerId);
  const { addRecords, drafts, removeDraft } = useEgsDraftsContext();
  const { accountAssets, optionState } = usePackageOptionsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState<EgsDraft | undefined>(undefined);

  const handleEdit = (draft: EgsDraft) => {
    setEditingDraft(draft);
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setEditingDraft(undefined);
  };

  const handleCommit = (records: Array<EgsDraftInput>) => {
    if (editingDraft) {
      removeDraft(editingDraft.id);
    }
    addRecords(records);
    enqueueSnackbar(
      records.length
        ? `Saved ${records.length} extra guest surcharge${records.length === 1 ? '' : 's'}`
        : 'No extra guest surcharges added',
      { variant: records.length ? 'success' : 'info' },
    );
    setEditingDraft(undefined);
  };

  const currency = useMemo(() => deriveOpportunityCurrency(data?.deals ?? []), [data?.deals]);
  const { audToSourceCurrencyRate, isLoadingByCurrency } = useGetAudExchangeRates([currency]);
  const isFxRateLoading = isLoadingByCurrency[currency] ?? false;
  const roomTypes = useMemo(() => {
    const syncedNames = [...(data?.deals ?? []), ...(data?.rooms ?? [])]
      .map((room) => room.room_asset?.name)
      .filter((name): name is string => !!name);
    const configuredNames = optionState.roomsByCategory.flatMap((rooms) => rooms.map((room) => room.assetName));
    const accountAssetNames = accountAssets.map((asset) => asset.name);
    return Array.from(new Set([...syncedNames, ...configuredNames, ...accountAssetNames].filter(Boolean)));
  }, [data?.deals, data?.rooms, optionState.roomsByCategory, accountAssets]);

  if (isLoading) {
    return null;
  }

  return (
    <TabPanelShell
      actions={
        <Button disabled={!roomTypes.length} onClick={() => setWizardOpen(true)} variant="contained">
          Configure EGS
        </Button>
      }
      subtitle="Configure surcharges for extra infants, children, and adults beyond included guests. This still saves to your session only - creating the record in Salesforce needs a backend endpoint that doesn't exist yet, so nothing here is submitted until that lands."
      title="Extra Guest Surcharges"
    >
      {relatedError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to load opportunity data, so the surcharge currency may be inaccurate.
        </Alert>
      )}

      <Box>
        <EgsTable drafts={drafts} onEdit={handleEdit} onRemove={removeDraft} />
      </Box>

      <EgsWizardModal
        audToSourceCurrencyRate={audToSourceCurrencyRate}
        currency={currency}
        editingDraft={editingDraft}
        isFxRateLoading={isFxRateLoading}
        onClose={handleCloseWizard}
        onCommit={handleCommit}
        open={wizardOpen}
        roomTypes={roomTypes}
      />
    </TabPanelShell>
  );
}
